import React, { useRef, useState, useLayoutEffect } from "react";
import "./style.scss";

interface TruncatedTooltipProps {
  children: React.ReactNode;
  tooltip: string;
  className?: string;
  id?: string;
  /** Max lines before truncating (default 1). Use 2+ for multi-line clamp. */
  maxLines?: number;
  /** Fill the parent width so ellipsis works inside table cells. */
  fullWidth?: boolean;
}

const TOOLTIP_PADDING = 8;
const VIEWPORT_PADDING = 12;

const TruncatedTooltip: React.FC<TruncatedTooltipProps> = ({
  children,
  tooltip,
  className = "",
  id,
  maxLines = 1,
  fullWidth = false,
}) => {
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const tooltipId = id || React.useId();
  const parentRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const getPosition = (): { top: number; left: number } => {
    if (!parentRef.current) return { top: 0, left: 0 };
    const rect = parentRef.current.getBoundingClientRect();
    return {
      top: rect.top - TOOLTIP_PADDING,
      left: rect.left + rect.width / 2,
    };
  };

  const clampToViewport = (top: number, left: number) => {
    const tw = tooltipRef.current?.offsetWidth ?? 200;
    const th = tooltipRef.current?.offsetHeight ?? 60;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // Tooltip is positioned with transform translate(-50%, -100%) so its bottom center is at (left, top)
    let clampedLeft = left;
    if (left - tw / 2 < VIEWPORT_PADDING)
      clampedLeft = VIEWPORT_PADDING + tw / 2;
    if (left + tw / 2 > vw - VIEWPORT_PADDING)
      clampedLeft = vw - VIEWPORT_PADDING - tw / 2;

    let clampedTop = top;
    const tooltipBottom = top;
    const tooltipTop = top - th;
    if (tooltipTop < VIEWPORT_PADDING) clampedTop = th + VIEWPORT_PADDING;
    if (tooltipBottom > vh - VIEWPORT_PADDING)
      clampedTop = vh - VIEWPORT_PADDING;

    return { top: clampedTop, left: clampedLeft };
  };

  const updatePosition = () => {
    const pos = getPosition();
    if (tooltipRef.current) {
      const clamped = clampToViewport(pos.top, pos.left);
      setPosition(clamped);
    } else {
      setPosition(pos);
    }
  };

  const openTooltip = () => {
    const pos = getPosition();
    setPosition(pos);
    setTooltipOpen(true);
  };

  useLayoutEffect(() => {
    if (tooltipOpen) {
      updatePosition();
      const rafId = requestAnimationFrame(() => updatePosition());
      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);
      return () => {
        cancelAnimationFrame(rafId);
        window.removeEventListener("scroll", updatePosition, true);
        window.removeEventListener("resize", updatePosition);
      };
    }
  }, [tooltipOpen]);

  return (
    <>
      <div
        className={`truncated-tooltip__parent${
          fullWidth ? " truncated-tooltip__parent--full" : ""
        }`}
        ref={parentRef}
      >
        <p
          className={`truncated-tooltip__text ${maxLines > 1 ? "truncated-tooltip__text--multiline" : ""} ${className}`}
          style={
            maxLines > 1
              ? ({ "--truncate-lines": maxLines } as React.CSSProperties)
              : undefined
          }
          id={tooltipId}
          tabIndex={0}
          aria-label={tooltip}
          onMouseEnter={openTooltip}
          onMouseLeave={() => setTooltipOpen(false)}
          onFocus={openTooltip}
          onBlur={() => setTooltipOpen(false)}
        >
          {children}
        </p>
      </div>

      {tooltipOpen && (
        <div
          ref={tooltipRef}
          className="fixed z-[9999] px-2 py-1 text-sm text-white bg-gray-900 rounded shadow-lg pointer-events-none max-w-[90vw]"
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`,
            transform: "translate(-50%, -100%)",
          }}
          role="tooltip"
        >
          {tooltip}
          <div
            className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"
            style={{ marginTop: "-1px" }}
          />
        </div>
      )}
    </>
  );
};

export default TruncatedTooltip;
